import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import FunnelHeader from "./FunnelHeader";
import StickyBottom from "./StickyBottom";
import LanguageStep from "./steps/LanguageStep";
import BasicDetailsStep from "./steps/BasicDetailsStep";
import CategoryMultiStep from "./steps/CategoryMultiStep";
import RecommendationsStep from "./steps/RecommendationsStep";
import DeliveryStep from "./steps/DeliveryStep";
import ConfirmationStep from "./steps/ConfirmationStep";

// Step indices:
// 1 Language | 2 BasicDetails | 3 Categories(multi) | 4 Recommendations |
// 5 Delivery+Payment+Confirm | 6 Confirmation
const TOTAL_STEPS = 5;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function genSessionId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function FunnelPage() {
  const { lang, dir, setLang, t } = useI18n();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [sessionId] = useState(genSessionId);
  const [recommendations, setRecommendations] = useState(null);
  const params = useParams();
  const query = useQuery();
  const repCodeOverride = params.repCode || query.get("to") || null;
  const categorySlugOverride = query.get("category") || null;

  const [order, setOrder] = useState({
    category_ids: [],
    name: "", age: "", gender: "",
    items: [],
    phone: "", alt_phone: "",
    address: "", city: "", district: "", state: "", pincode: "",
    payment_method: "online",
  });

  const [submission, setSubmission] = useState(null);

  useEffect(() => {
    api.get("/public/config").then((r) => {
      setConfig(r.data);
      const stored = localStorage.getItem("elvis_lang");
      const found = r.data.languages.find((l) => l.code === stored);
      if (!found && r.data.languages.length) {
        setLang(r.data.languages[0].code, r.data.languages[0].dir || "ltr");
      }
      // Honour ?category=<slug> deeplink — pre-select category and skip to step 2 (basic) on load
      if (categorySlugOverride) {
        const cat = r.data.categories.find((c) => c.slug === categorySlugOverride);
        if (cat) {
          setOrder((o) => ({ ...o, category_ids: o.category_ids.includes(cat.id) ? o.category_ids : [...o.category_ids, cat.id] }));
        }
      }
    });
    api.get("/public/form_config").then((r) => setFormConfig(r.data)).catch(() => setFormConfig({}));
  }, []); // eslint-disable-line

  // Track step events
  useEffect(() => {
    api.post("/public/track", { step: `step_${step}`, language: lang, session_id: sessionId }).catch(() => {});
  }, [step, lang, sessionId]);

  // Load recommendations when entering step 4
  useEffect(() => {
    if (step === 4 && order.category_ids.length > 0) {
      setRecommendations(null);
      api.get("/public/recommendations", { params: { category_ids: order.category_ids.join(",") } })
        .then((r) => setRecommendations(r.data))
        .catch(() => setRecommendations({ video_url: "", products: [], combos: [], collections: [] }));
    }
  }, [step, order.category_ids]);

  const langs = config?.languages || [];
  const categories = config?.categories || [];
  const settings = config?.settings || {};

  const totalAmount = useMemo(
    () => order.items.reduce((s, it) => s + (it.price || 0) * (it.qty || 1), 0),
    [order.items]
  );
  const itemCount = useMemo(
    () => order.items.reduce((s, it) => s + (it.qty || 1), 0),
    [order.items]
  );

  const next = () => setStep((s) => Math.min(s + 1, 6));
  const back = () => setStep((s) => Math.max(s - 1, 1));
  const update = (patch) => setOrder((o) => ({ ...o, ...patch }));

  const toggleCategory = (cid) => {
    setOrder((o) => ({
      ...o,
      category_ids: o.category_ids.includes(cid)
        ? o.category_ids.filter((x) => x !== cid)
        : [...o.category_ids, cid],
    }));
  };

  const submit = async () => {
    const payload = {
      name: order.name,
      age: order.age ? Number(order.age) : null,
      gender: order.gender || null,
      category_ids: order.category_ids,
      concern_ids: [],
      items: order.items.map((it) => ({
        product_id: it.product_id, name: it.name, price: it.price, qty: it.qty, kind: it.kind || "product",
      })),
      total: totalAmount,
      phone: order.phone,
      alt_phone: order.alt_phone || null,
      address: order.address,
      city: order.city,
      district: order.district,
      state: order.state,
      pincode: order.pincode,
      payment_method: order.payment_method,
      language: lang,
      rep_code_override: repCodeOverride,
    };
    // Open a placeholder window SYNCHRONOUSLY within the user gesture, then redirect once we have the wa_url.
    // This is the only reliable way to launch WhatsApp on iOS Safari & many Android browsers
    // since post-await window.open is blocked as a non-user-gesture popup.
    const waWindow = window.open("about:blank", "_blank");
    let data;
    try {
      const resp = await api.post("/public/leads", payload);
      data = resp.data;
    } catch (e) {
      if (waWindow) waWindow.close();
      throw e;
    }
    setSubmission(data);
    api.post(`/public/leads/${data.lead_id}/wa_opened`).catch(() => {});
    if (waWindow && !waWindow.closed) {
      try { waWindow.location.href = data.wa_url; }
      catch (_) { window.location.href = data.wa_url; }
    } else {
      // popup blocked → same-tab navigation (always works on mobile)
      window.location.href = data.wa_url;
    }
    setStep(6);
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bone">
        <p className="font-serif text-2xl text-brand-dark">Elvis Beauty</p>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div className="min-h-[100dvh] bg-brand-bone">
        <LanguageStep
          languages={langs}
          currentLang={lang}
          onSelect={(code, d) => { setLang(code, d); next(); }}
        />
      </div>
    );
  }

  // Steps 4-5 show the persistent sticky cart bar (recs only — step 5 has its own confirm button)
  return (
    <div className="min-h-[100dvh] bg-brand-bone" dir={dir}>
      <FunnelHeader
        step={step > 5 ? 5 : step}
        totalSteps={TOTAL_STEPS}
        onBack={back}
        canBack={step > 1 && step < 6}
        lang={lang}
        langs={langs}
        onLangChange={(code, d) => setLang(code, d || "ltr")}
      />
      <main className="max-w-2xl mx-auto px-5 py-4 sm:py-8 pb-40" data-testid="funnel-main">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {step === 2 && (
              <BasicDetailsStep value={order} onChange={update} onSubmit={next} t={t} formStep={formConfig?.basic} />
            )}
            {step === 3 && (
              <CategoryMultiStep
                categories={categories}
                selected={order.category_ids}
                onToggle={toggleCategory}
                onContinue={next}
              />
            )}
            {step === 4 && (
              <RecommendationsStep
                recommendations={recommendations}
                items={order.items}
                onChangeItems={(items) => update({ items })}
                t={t}
              />
            )}
            {step === 5 && (
              <DeliveryStep
                value={order}
                onChange={update}
                onSubmit={submit}
                t={t}
                formStep={formConfig?.delivery}
                total={totalAmount}
                items={order.items}
                settings={settings}
              />
            )}
            {step === 6 && submission && (
              <ConfirmationStep
                submission={submission}
                onAgain={() => {
                  setOrder({
                    category_ids: [], name: "", age: "", gender: "",
                    items: [], phone: "", alt_phone: "", address: "", city: "", district: "", state: "", pincode: "", payment_method: "online",
                  });
                  setSubmission(null);
                  setStep(2);
                }}
                t={t}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Sticky cart bar shown only on Recommendations step (5 has its own submit) */}
      {step === 4 && (
        <StickyBottom
          count={itemCount}
          total={totalAmount}
          ctaLabel={itemCount === 0 ? t("recs.empty") : `${t("common.continue")} →`}
          disabled={itemCount === 0}
          onCta={next}
          testId="recs-continue-btn"
        />
      )}
    </div>
  );
}
